<script setup>
import { computed } from 'vue';
import { useDemoEvents } from '@/composables/useDemoEvents';
import Icon from '@/components/ui/Icon.vue';

const feed = useDemoEvents();

const rows = computed(() => feed.events.slice(0, 6));
const time = (d) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
</script>

<template>
  <div class="log">
    <div class="log-head">
      <span class="tab">Saves</span>
      <span class="where"><Icon name="info" :size="13" />Stored in this browser</span>
    </div>

    <TransitionGroup v-if="rows.length" name="row" tag="ul" class="rows">
      <li v-for="e in rows" :key="e.id" class="row">
        <span class="time">{{ time(e.time) }}</span>
        <span class="what">{{ e.text }}</span>
        <span class="sent">0 B sent</span>
      </li>
    </TransitionGroup>
    <div v-else class="empty">
      <span class="wide-only">Change something in the demo above and each save appears here.</span>
      <span class="narrow-only">On a laptop, edit the demo above and watch each save appear here.</span>
    </div>

    <div class="log-foot">
      <span><strong>{{ feed.events.length }}</strong> change{{ feed.events.length === 1 ? '' : 's' }} saved</span>
      <span class="zero"><strong>0 bytes</strong> sent to a server</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.log {
  border: 1px solid var(--line);
  border-radius: var(--radius-l);
  background: var(--surface);
  box-shadow: 0 24px 48px -28px rgba(23, 20, 42, 0.25);
  overflow: hidden;
}

.log-head,
.log-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  font-size: 12px;
}

.log-head {
  border-bottom: 1px solid var(--line);
}

.tab {
  font-weight: 600;
}

.where {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-3);
}

.rows {
  list-style: none;
  margin: 0;
  padding: 6px 0;
  min-height: 228px;
}

.row {
  display: grid;
  grid-template-columns: 64px 1fr auto;
  gap: 12px;
  align-items: baseline;
  padding: 7px 16px;
  font-size: 13px;
}

.time {
  font-size: 11px;
  color: var(--text-3);
  font-variant-numeric: tabular-nums;
}

.what {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sent {
  font-size: 11px;
  color: #13692f;
  font-weight: 600;
}

.empty {
  display: grid;
  place-items: center;
  min-height: 240px;
  padding: 24px;
  color: var(--text-3);
  font-size: 13px;
  text-align: center;
}

.narrow-only {
  display: none;
}

@media (max-width: 1023px) {
  .empty {
    min-height: 140px;
  }

  .wide-only {
    display: none;
  }

  .narrow-only {
    display: inline;
  }
}

.log-foot {
  border-top: 1px solid var(--line);
  background: var(--surface-2);
  color: var(--text-2);

  strong {
    color: var(--text);
    font-weight: 600;
  }
}

.zero strong {
  color: #13692f;
}

.row-enter-active {
  transition: opacity 0.3s, transform 0.3s, background-color 1.2s;
}

.row-enter-from {
  opacity: 0;
  transform: translateY(-6px);
  background: var(--iris-soft);
}

.row-leave-active {
  display: none;
}
</style>
